import { Mail, MapPin, Phone, Send } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { connect } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DefaultAvatar } from "../../components/common/DefaultAvatar";
import { Error } from "../../components/common/Error";
import { PageNavigation } from "../../components/common/PageNavigation";
import { HHTabs, TabsMap } from "../../components/common/Tabs";
import { API_ENDPOINTS } from "../../constants/common";
import { RootState } from "../../store";
import { selectUserData, selectUserInfo } from "../../store/selectors";
import { apiClient, formatPhoneForDisplay, getPhoneHref } from "../../utils";
import {
  restoreClassifiedsScrollPosition,
  saveClassifiedsScrollPosition,
} from "../../utils/classifiedsScrollRestoration";
import { resolveListingTotalPrice } from "../../utils/listingPrice";

type ListingType = "house" | "land_plot" | "apartment" | "commercial";
type MarketType = "sale" | "rent";
type ModerationStatus = "pending" | "approved" | "rejected";

type Listing = {
  id: number | string;
  type: ListingType;
  status_id?: number | string;
  moderation_status?: ModerationStatus;
  moderation_reason?: string | null;
  title: string;
  market_type: MarketType;
  board_category: string;
  board_subcategory?: string | null;
  image_url: string | null;
  area_sqm: number | string | null;
  area_site: number | string | null;
  price_sqm: number | string | null;
  price_total?: number | string | null;
  price_monthly: number | string | null;
};

type UserProfile = Record<string, any> | null;
type UserNode = {
  id?: number | string;
  user_id?: number | string;
  userId?: number | string;
  abilities?: string[];
  data?: UserNode;
  profile?: UserNode;
  user?: UserNode;
} | null;

type Props = {
  user: UserNode;
  userData: UserNode;
};

type ListingPagination = {
  limit: number;
  has_more: boolean;
  next_cursor: string | null;
  total: number | null;
};

const PUBLISHED_STATUS_ID = 1;
const UNPUBLISHED_STATUS_ID = 2;
const PAGE_LIMIT = 10;
type UserListingsTab = "published" | "moderation" | "unpublished";
type UserListingsCounts = Record<UserListingsTab, number>;

const TYPE_LABELS: Record<ListingType, string> = {
  house: "Дом",
  land_plot: "Земельный участок",
  apartment: "Квартира",
  commercial: "Коммерция",
};

const CATEGORY_LABELS: Record<string, string> = {
  houses: "Дома",
  land_plots: "Земельные участки",
  assignments: "Переуступки",
  secondary: "Вторичное жилье",
  apartments: "Квартиры",
  commercial: "Коммерция",
  dnt: "ДНТ",
  snt: "СНТ",
  izhs: "ИЖС",
  parking: "Паркинг",
};

const MODERATION_LABELS: Record<ModerationStatus, string> = {
  pending: "На модерации",
  approved: "Модерация пройдена",
  rejected: "Отклонено",
};

const getString = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const normalizeId = (value: unknown): string => {
  const raw = getString(value);
  if (!raw || raw === "undefined" || raw === "null") return "";
  if (/^\d+$/.test(raw)) return raw.replace(/^0+(?=\d)/, "");

  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? String(parsed) : raw;
};

const getListingKey = (item: Listing): string => `${item.type}:${item.id}`;

const hasUserAbility = (ability: string, ...nodes: UserNode[]): boolean => {
  const stack = nodes.filter(Boolean) as NonNullable<UserNode>[];
  const visited = new Set<NonNullable<UserNode>>();

  while (stack.length) {
    const node = stack.pop();
    if (!node || visited.has(node)) continue;
    visited.add(node);

    if (Array.isArray(node.abilities) && node.abilities.includes(ability)) {
      return true;
    }

    if (node.data) stack.push(node.data);
    if (node.profile) stack.push(node.profile);
    if (node.user) stack.push(node.user);
  }

  return false;
};

const getProfileName = (profile: UserProfile, userId: string): string => {
  const candidates = [
    [profile?.firstname, profile?.lastname].filter(Boolean).join(" "),
    [profile?.first_name, profile?.last_name].filter(Boolean).join(" "),
    [profile?.firstName, profile?.lastName].filter(Boolean).join(" "),
    [profile?.name, profile?.surname].filter(Boolean).join(" "),
    profile?.name,
    profile?.full_name,
    profile?.fullName,
    profile?.email,
    profile?.phone,
  ];

  for (const candidate of candidates) {
    const value = getString(candidate);
    if (value) return value;
  }

  return `Пользователь #${userId}`;
};

const getProfileContact = (profile: UserProfile, key: string): string => {
  const direct = getString(profile?.[key]);
  if (direct) return direct;

  const socials = Array.isArray(profile?.socials) ? profile?.socials : [];
  const social = socials.find((item: any) => item?.type === key || item?.code === key || item?.name === key);
  return getString(social?.value);
};

const getProfileAvatar = (profile: UserProfile): string => {
  return getString(profile?.avatar_url || profile?.avatarUrl || profile?.avatar || profile?.photo_url || profile?.image_url);
};

const getProfileRegistrationDate = (profile: UserProfile): string | null => {
  const rawValue = profile?.created_at ?? profile?.createdAt ?? profile?.registered_at ?? profile?.registeredAt;
  if (!rawValue) return null;

  const date = new Date(String(rawValue));
  if (Number.isNaN(date.getTime())) return null;

  return new Intl.DateTimeFormat("ru-RU", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
};

const formatMoney = (value: unknown): string | null => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return `${new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 }).format(parsed)} ₽`;
};

const formatAreaValue = (value: unknown, unit: "sqm" | "sotka"): string | null => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  if (unit === "sotka") {
    return `${new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 2 }).format(parsed / 100)} сот.`;
  }
  return `${new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 1 }).format(parsed)} м²`;
};

const getListingCategory = (item: Listing): string => {
  return CATEGORY_LABELS[item.board_subcategory || ""] || CATEGORY_LABELS[item.board_category] || TYPE_LABELS[item.type];
};

const getListingPrice = (item: Listing): { primary: string; descriptor: string | null } => {
  const monthly = formatMoney(item.price_monthly);
  const priceSqm = Number(item.price_sqm);
  const saleArea = Number(item.type === "land_plot" ? item.area_site : item.area_sqm);
  const unitPrice = item.type === "land_plot" ? priceSqm * 100 : priceSqm;
  const unitPriceText = formatMoney(unitPrice);
  const unitLabel = item.type === "land_plot" ? "сотку" : "м²";

  if (item.market_type === "rent") {
    return { primary: monthly ? `${monthly}/мес` : "Договорная", descriptor: null };
  }

  const total = resolveListingTotalPrice({
    area: saleArea,
    pricePerSqm: priceSqm,
    explicitTotalPrice: item.price_total,
  });

  return {
    primary: formatMoney(total) || unitPriceText || "Договорная",
    descriptor: unitPriceText ? `${unitPriceText} за ${unitLabel}` : null,
  };
};

const isPublished = (item: Listing): boolean =>
  Number(item.status_id ?? PUBLISHED_STATUS_ID) === PUBLISHED_STATUS_ID &&
  item.moderation_status !== "pending" &&
  item.moderation_status !== "rejected";
const isInModeration = (item: Listing): boolean =>
  item.moderation_status === "pending" || item.moderation_status === "rejected";
const getUserListingsTab = (item: Listing): UserListingsTab => {
  if (isInModeration(item)) return "moderation";
  return isPublished(item) ? "published" : "unpublished";
};

const parseUserListingsCounts = (value: unknown): UserListingsCounts | null => {
  if (!value || typeof value !== "object") return null;

  const source = value as Record<string, unknown>;
  const parsed = (tab: UserListingsTab): number | null => {
    const count = Number(source[tab]);
    return Number.isFinite(count) && count >= 0 ? count : null;
  };
  const published = parsed("published");
  const moderation = parsed("moderation");
  const unpublished = parsed("unpublished");

  return published === null || moderation === null || unpublished === null
    ? null
    : { published, moderation, unpublished };
};

const isClassifiedListingPath = (value: string): boolean =>
  /^\/classifieds\/(?:shared\/[^/?#]+|(?:house|land_plot|apartment|commercial)\/[^/?#]+)(?:\?.*)?(?:#.*)?$/.test(value);

const isControlPanelUserPath = (value: string): boolean =>
  /^\/control-panel\/users\/[^/?#]+\/?$/.test(value);

const ListingCard: React.FC<{
  item: Listing;
  returnTo: string;
  canManageListings: boolean;
  canDeleteListings: boolean;
  publicationPending: boolean;
  publicationError?: string;
  deletePending: boolean;
  deleteError?: string;
  onTogglePublication: (item: Listing) => void;
  onDelete: (item: Listing) => void;
}> = ({
  item,
  returnTo,
  canManageListings,
  canDeleteListings,
  publicationPending,
  publicationError,
  deletePending,
  deleteError,
  onTogglePublication,
  onDelete,
}) => {
  const navigate = useNavigate();
  const price = getListingPrice(item);
  const areaDetails = [
    formatAreaValue(item.area_sqm, "sqm") ? `Площадь: ${formatAreaValue(item.area_sqm, "sqm")}` : null,
    formatAreaValue(item.area_site, "sotka") ? `Участок: ${formatAreaValue(item.area_site, "sotka")}` : null,
  ].filter(Boolean);
  const published = isPublished(item);
  const moderationStatus = item.moderation_status;

  const openListing = () => {
    saveClassifiedsScrollPosition(returnTo);
    navigate(`/classifieds/${item.type}/${item.id}`, {
      state: { classifiedsReturnTo: returnTo },
    });
  };

  return (
    <article
      className="flex h-full cursor-pointer flex-col overflow-hidden rounded-none border-y border-[#e8e8e8] bg-white shadow-[0_1px_3px_rgba(0,0,0,0.07),0_1px_8px_rgba(0,0,0,0.04)] transition duration-200 hover:-translate-y-[1px] hover:shadow-[0_4px_16px_rgba(200,80,138,0.12),0_1px_4px_rgba(0,0,0,0.08)] md:rounded-[18px] md:border"
      role="link"
      tabIndex={0}
      onClick={openListing}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          openListing();
        }
      }}
    >
      <div className="relative h-[200px] w-full overflow-hidden bg-[#ddd]">
        {item.image_url ? (
          <img className="block h-full w-full object-cover" src={item.image_url} alt={item.title} />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-[13px] text-[#6b6b6b]">Нет фото</div>
        )}
      </div>
      <div className="grid flex-1 content-start gap-2 px-4 pb-4 pt-3.5">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="rounded-full bg-[#fbeaf0] px-2.5 py-1 text-[11px] font-semibold text-[#8b2a5e]">
            {item.market_type === "rent" ? "Аренда" : "Купля/Продажа"}
          </span>
          <span className="rounded-full bg-[#f2f2f2] px-2.5 py-1 text-[11px] font-semibold text-[#5f5f5f]">
            {getListingCategory(item)}
          </span>
          {canManageListings ? (
            <span
              className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                published ? "bg-[#fbeaf0] text-[#8b2a5e]" : "bg-[#eeeeee] text-[#777]"
              }`}
            >
              {moderationStatus && moderationStatus !== "approved"
                ? MODERATION_LABELS[moderationStatus]
                : published
                  ? "Опубликовано"
                  : "Не опубликовано"}
            </span>
          ) : null}
        </div>
        <h3 className="m-0 min-h-[40px] text-[15px] leading-[1.35] tracking-[-0.2px] text-[#1a1a1a] line-clamp-2">
          {item.title}
        </h3>
        <div className="text-[22px] font-bold leading-[1.15] text-[#8b2a5e]">{price.primary}</div>
        <div className="min-h-[18px] text-xs leading-[1.45] text-[#6b6b6b]">{price.descriptor || "\u00A0"}</div>
        {areaDetails.length ? <div className="text-xs leading-[1.45] text-[#6b6b6b]">{areaDetails.join(" · ")}</div> : null}
        {moderationStatus === "rejected" && item.moderation_reason ? (
          <div className="rounded-lg border border-[#f5c2c7] bg-[#fff5f5] px-3 py-2 text-[12px] leading-5 text-[#9f1239]">
            Причина отказа: {item.moderation_reason}
          </div>
        ) : null}
        {canManageListings ? (
          <div className="mt-1 flex flex-wrap items-center justify-between gap-2 border-t border-[#efefef] pt-3">
            <span className={`inline-flex items-center gap-1.5 text-[12px] font-medium ${published ? "text-[#8b2a5e]" : "text-[#777]"}`}>
              <span
                aria-hidden="true"
                className={`h-2 w-2 rounded-full ${
                  published ? "bg-[#c8508a]" : moderationStatus === "rejected" ? "bg-[#e11d48]" : "bg-[#b8b8b8]"
                }`}
              />
              {moderationStatus && moderationStatus !== "approved"
                ? MODERATION_LABELS[moderationStatus]
                : published
                  ? "Опубликовано"
                  : "Снято с публикации"}
            </span>
            <button
              type="button"
              className={`h-[34px] cursor-pointer rounded-md border px-3 text-[12px] font-semibold leading-none transition disabled:cursor-wait disabled:opacity-60 ${
                published
                  ? "border-[#dedede] bg-white text-[#555] hover:border-[#c8508a] hover:text-[#8b2a5e]"
                  : "border-[#c8508a] bg-[#c8508a] text-white hover:border-[#b3407a] hover:bg-[#b3407a]"
              }`}
              disabled={publicationPending || Boolean(moderationStatus && moderationStatus !== "approved")}
              aria-busy={publicationPending}
              onClick={(event) => {
                event.stopPropagation();
                onTogglePublication(item);
              }}
            >
              {moderationStatus && moderationStatus !== "approved"
                ? "Ждёт решения"
                : publicationPending
                ? published
                  ? "Снимаем..."
                  : "Публикуем..."
                : published
                  ? "Снять с публикации"
                  : "Опубликовать"}
            </button>
            <div className="flex w-full flex-wrap gap-2">
              <button
                type="button"
                className="h-[34px] cursor-pointer rounded-md border border-[#c8508a] bg-white px-3 text-[12px] font-semibold leading-none text-[#8b2a5e] transition hover:bg-[#fff2f8]"
                onClick={(event) => {
                  event.stopPropagation();
                  navigate(`/classifieds/${item.type}/${item.id}/edit`);
                }}
              >
                Редактировать
              </button>
              {canDeleteListings ? (
                <button
                  type="button"
                  className="h-[34px] cursor-pointer rounded-md border border-rose-300 bg-white px-3 text-[12px] font-semibold leading-none text-rose-600 transition hover:bg-rose-50 disabled:cursor-wait disabled:opacity-60"
                  disabled={deletePending}
                  aria-busy={deletePending}
                  onClick={(event) => {
                    event.stopPropagation();
                    onDelete(item);
                  }}
                >
                  {deletePending ? "Удаляем..." : "Удалить"}
                </button>
              ) : null}
            </div>
            {publicationError ? <span className="w-full text-[12px] leading-[1.4] text-[#b42318]">{publicationError}</span> : null}
            {deleteError ? <span className="w-full text-[12px] leading-[1.4] text-[#b42318]">{deleteError}</span> : null}
          </div>
        ) : null}
      </div>
    </article>
  );
};

const _ClassifiedsUserListings: React.FC<Props> = ({ user, userData }) => {
  const { userId = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [cookies] = useCookies(["userToken"]);
  const requestIdRef = useRef(0);
  const isControlPanelProfile = isControlPanelUserPath(location.pathname);
  const controlPanelPath = "/control-panel";
  const navigationState = location.state as {
    listingReturnTo?: string;
    controlPanelReturn?: boolean;
    controlPanelPath?: string;
  } | null;
  const returnTo = isControlPanelProfile
    ? `/control-panel/users/${userId}`
    : `/classifieds/users/${userId}`;
  const defaultBackTo = isControlPanelProfile
    ? navigationState?.controlPanelPath || controlPanelPath
    : "/classifieds";
  const defaultBackLabel = isControlPanelProfile ? "В контроль-панель" : "К объявлениям";
  const shouldReturnToControlPanel = isControlPanelProfile && Boolean(navigationState?.controlPanelReturn);
  const hasPreviousHistoryEntry = Number(window.history.state?.idx) > 0;
  const listingReturnTo =
    hasPreviousHistoryEntry &&
    typeof navigationState?.listingReturnTo === "string" &&
    isClassifiedListingPath(navigationState.listingReturnTo)
      ? navigationState.listingReturnTo
      : null;
  const goBack = () => {
    if (listingReturnTo) {
      navigate(listingReturnTo);
      return;
    }
    if (shouldReturnToControlPanel) {
      navigate(-1);
      return;
    }
    navigate(defaultBackTo);
  };
  const isManager = hasUserAbility("manager", userData, user);
  const hasSessionToken = Boolean(String(cookies.userToken || userData?.token || "").trim());
  const isSessionRestoring = isControlPanelProfile && hasSessionToken && !user;
  const canAccessControlPanelProfile = !isControlPanelProfile || isManager;
  const canManageListings = isManager;
  const canDeleteListings = canManageListings;
  const [profile, setProfile] = useState<UserProfile>(null);
  const [items, setItems] = useState<Listing[]>([]);
  const [pagination, setPagination] = useState<ListingPagination>({
    limit: PAGE_LIMIT,
    has_more: false,
    next_cursor: null,
    total: null,
  });
  const [listingCounts, setListingCounts] = useState<UserListingsCounts | null>(null);
  const [pending, setPending] = useState(true);
  const [loadMorePending, setLoadMorePending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const [listingStatusPendingKeys, setListingStatusPendingKeys] = useState<Set<string>>(new Set());
  const [listingStatusErrors, setListingStatusErrors] = useState<Record<string, string>>({});
  const [listingDeletePendingKeys, setListingDeletePendingKeys] = useState<Set<string>>(new Set());
  const [listingDeleteErrors, setListingDeleteErrors] = useState<Record<string, string>>({});
  const [activeListingsTab, setActiveListingsTab] = useState<UserListingsTab>("published");
  const [autoLoadTab, setAutoLoadTab] = useState<UserListingsTab | null>(null);
  const autoLoadCursorRef = useRef<string | null>(null);

  const loadListings = useCallback(
    async (cursor: string | null = null) => {
      if (isSessionRestoring) return;
      if (!canAccessControlPanelProfile) {
        setError("Недостаточно прав для просмотра профиля из контроль-панели");
        setPending(false);
        return;
      }

      const normalizedUserId = normalizeId(userId);
      if (!normalizedUserId) {
        setError("Некорректная ссылка на пользователя");
        setPending(false);
        return;
      }

      const append = Boolean(cursor);
      const requestId = ++requestIdRef.current;
      if (append) {
        setLoadMorePending(true);
        setLoadMoreError(null);
      } else {
        setPending(true);
        setError(null);
        setLoadMoreError(null);
      }

      try {
        const query = new URLSearchParams({
          limit: String(PAGE_LIMIT),
        });
        if (cursor) query.set("cursor", cursor);
        const response = await apiClient.get(`${API_ENDPOINTS.classifiedsUserListings(normalizedUserId)}?${query}`);

        if (requestId !== requestIdRef.current) return;

        const nextItems: Listing[] = Array.isArray(response?.data?.data) ? response.data.data : [];
        const nextPagination = response?.data?.meta?.pagination;
        const nextListingCounts = parseUserListingsCounts(response?.data?.meta?.counts);
        if (nextListingCounts) {
          setListingCounts(nextListingCounts);
        }
        if (!append) {
          const responseProfile = response?.data?.meta?.user;
          setProfile(
            responseProfile && typeof responseProfile === "object"
              ? responseProfile
              : { id: normalizedUserId, name: `Пользователь #${normalizedUserId}` },
          );
        }
        setItems((current) => {
          if (!append) return nextItems;
          const known = new Set(current.map((item) => `${item.type}:${item.id}`));
          return [...current, ...nextItems.filter((item) => !known.has(`${item.type}:${item.id}`))];
        });
        const rawTotal = response?.data?.meta?.total;
        setPagination({
          limit: Number(nextPagination?.limit) || PAGE_LIMIT,
          has_more: Boolean(nextPagination?.has_more),
          next_cursor: typeof nextPagination?.next_cursor === "string" ? nextPagination.next_cursor : null,
          total: rawTotal !== null && rawTotal !== undefined && Number.isFinite(Number(rawTotal))
            ? Number(rawTotal)
            : null,
        });
      } catch (requestError: any) {
        const message =
          requestError?.response?.status === 404
            ? "Пользователь не найден"
            : requestError?.response?.data?.error || "Не удалось загрузить объявления пользователя";
        if (append) setLoadMoreError(message);
        else setError(message);
      } finally {
        if (requestId === requestIdRef.current) {
          if (append) setLoadMorePending(false);
          else setPending(false);
        }
      }
    },
    [canAccessControlPanelProfile, isSessionRestoring, userId],
  );

  useEffect(() => {
    loadListings();
  }, [loadListings]);

  useEffect(() => {
    if (pending) return;
    return restoreClassifiedsScrollPosition(returnTo);
  }, [items.length, pending, returnTo]);

  const adjustListingCounts = useCallback((changes: Partial<Record<UserListingsTab, number>>) => {
    setListingCounts((current) => {
      if (!current) return current;
      return {
        published: Math.max(0, current.published + (changes.published || 0)),
        moderation: Math.max(0, current.moderation + (changes.moderation || 0)),
        unpublished: Math.max(0, current.unpublished + (changes.unpublished || 0)),
      };
    });
  }, []);

  const toggleListingPublication = useCallback(
    async (item: Listing) => {
      if (!canManageListings) return;

      const key = getListingKey(item);
      if (listingStatusPendingKeys.has(key)) return;

      const nextStatusId = isPublished(item) ? UNPUBLISHED_STATUS_ID : PUBLISHED_STATUS_ID;

      setListingStatusPendingKeys((current) => new Set(current).add(key));
      setListingStatusErrors((current) => {
        if (!current[key]) return current;
        const next = { ...current };
        delete next[key];
        return next;
      });

      try {
        const response = await apiClient.patch(API_ENDPOINTS.classifiedsListing(item.type, item.id), {
          status_id: nextStatusId,
        });
        const responseStatusId = Number(response?.data?.data?.status_id);
        const resolvedStatusId = Number.isFinite(responseStatusId) ? responseStatusId : nextStatusId;
        const previousTab = getUserListingsTab(item);
        const nextTab = getUserListingsTab({ ...item, status_id: resolvedStatusId });
        if (previousTab !== nextTab) {
          adjustListingCounts({ [previousTab]: -1, [nextTab]: 1 });
        }
        setItems((current) =>
          current.map((currentItem) =>
            getListingKey(currentItem) === key ? { ...currentItem, status_id: resolvedStatusId } : currentItem,
          ),
        );
      } catch (requestError: any) {
        setListingStatusErrors((current) => ({
          ...current,
          [key]: requestError?.response?.data?.error || "Не удалось изменить статус публикации",
        }));
      } finally {
        setListingStatusPendingKeys((current) => {
          const next = new Set(current);
          next.delete(key);
          return next;
        });
      }
    },
    [adjustListingCounts, canManageListings, listingStatusPendingKeys],
  );

  const deleteListing = useCallback(
    async (item: Listing) => {
      if (!canDeleteListings) return;

      const key = getListingKey(item);
      if (listingDeletePendingKeys.has(key)) return;

      const shouldDelete = window.confirm(`Удалить объявление «${item.title}»?`);
      if (!shouldDelete) return;

      setListingDeletePendingKeys((current) => new Set(current).add(key));
      setListingDeleteErrors((current) => {
        if (!current[key]) return current;
        const next = { ...current };
        delete next[key];
        return next;
      });

      try {
        await apiClient.delete(API_ENDPOINTS.classifiedsListing(item.type, item.id));
        adjustListingCounts({ [getUserListingsTab(item)]: -1 });
        setItems((current) => current.filter((currentItem) => getListingKey(currentItem) !== key));
      } catch (requestError: any) {
        setListingDeleteErrors((current) => ({
          ...current,
          [key]: requestError?.response?.data?.error || "Не удалось удалить объявление",
        }));
      } finally {
        setListingDeletePendingKeys((current) => {
          const next = new Set(current);
          next.delete(key);
          return next;
        });
      }
    },
    [adjustListingCounts, canDeleteListings, listingDeletePendingKeys],
  );

  const grouped = useMemo(() => {
    const moderation = items.filter(isInModeration);
    const published = items.filter((item) => !isInModeration(item) && isPublished(item));
    const unpublished = items.filter((item) => !isInModeration(item) && !isPublished(item));
    return { published, moderation, unpublished };
  }, [items]);
  const listingTabs = useMemo<TabsMap>(
    () => ({
      published: {
        id: "published",
        name: `Опубликовано: ${listingCounts?.published ?? grouped.published.length}`,
      },
      moderation: {
        id: "moderation",
        name: `На модерации: ${listingCounts?.moderation ?? grouped.moderation.length}`,
      },
      unpublished: {
        id: "unpublished",
        name: `Не опубликовано: ${listingCounts?.unpublished ?? grouped.unpublished.length}`,
      },
    }),
    [
      grouped.moderation.length,
      grouped.published.length,
      grouped.unpublished.length,
      listingCounts?.moderation,
      listingCounts?.published,
      listingCounts?.unpublished,
    ],
  );
  const visibleItems =
    canManageListings ? grouped[activeListingsTab] : grouped.published;
  const visibleTitle =
    canManageListings && activeListingsTab === "moderation"
      ? "Объявления на модерации"
      : canManageListings && activeListingsTab === "unpublished"
        ? "Неопубликованные объявления"
        : "Опубликованные объявления";
  const emptyText =
    canManageListings && activeListingsTab === "moderation"
      ? "Объявлений на модерации нет"
      : canManageListings && activeListingsTab === "unpublished"
        ? "Неопубликованных объявлений нет"
        : "Опубликованных объявлений нет";
  const handleListingsTabChange = useCallback((id: string) => {
    if (id !== "published" && id !== "moderation" && id !== "unpublished") return;

    setActiveListingsTab(id);
    autoLoadCursorRef.current = null;
    setAutoLoadTab(
      listingCounts && grouped[id].length < listingCounts[id] ? id : null,
    );
  }, [grouped, listingCounts]);

  useEffect(() => {
    if (!autoLoadTab || !listingCounts) return;

    const loadedCount = grouped[autoLoadTab].length;
    const targetCount = listingCounts[autoLoadTab];
    const nextCursor = pagination.next_cursor;

    if (loadedCount >= targetCount || !pagination.has_more || !nextCursor || loadMoreError) {
      setAutoLoadTab(null);
      autoLoadCursorRef.current = null;
      return;
    }
    if (loadMorePending || autoLoadCursorRef.current === nextCursor) return;

    autoLoadCursorRef.current = nextCursor;
    void loadListings(nextCursor);
  }, [
    autoLoadTab,
    grouped,
    listingCounts,
    loadListings,
    loadMoreError,
    loadMorePending,
    pagination.has_more,
    pagination.next_cursor,
  ]);

  const displayName = getProfileName(profile, normalizeId(userId));
  const avatarUrl = getProfileAvatar(profile);
  const rawPhone = getProfileContact(profile, "phone");
  const phone = formatPhoneForDisplay(rawPhone);
  const phoneHref = getPhoneHref(rawPhone);
  const email = getProfileContact(profile, "email");
  const telegram = getProfileContact(profile, "telegram");
  const registrationDate = getProfileRegistrationDate(profile);
  const listingsCount = listingCounts
    ? listingCounts.published + listingCounts.moderation + listingCounts.unpublished
    : pagination.total !== null
      ? pagination.total
      : null;
  const visibleListingsCount = canManageListings
    ? listingCounts?.[activeListingsTab] ?? null
    : listingsCount;

  if (pending) {
    return (
      <main className="mx-auto grid max-w-[1344px] gap-5 px-3.5 py-6 md:px-6 lg:px-10 xl:px-12">
        <PageNavigation
          backLabel={listingReturnTo ? "К объявлению" : defaultBackLabel}
          onBack={goBack}
        />
        <div className="h-36 animate-pulse rounded-2xl bg-[#ececec]" />
        <div className="grid animate-pulse gap-4 md:grid-cols-2 xl:grid-cols-3">
          <div className="h-[320px] rounded-2xl bg-[#ececec]" />
          <div className="h-[320px] rounded-2xl bg-[#ececec]" />
          <div className="h-[320px] rounded-2xl bg-[#ececec]" />
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="mx-auto grid max-w-[900px] gap-6 px-3.5 py-10 md:px-6">
        <PageNavigation
          backLabel={listingReturnTo ? "К объявлению" : defaultBackLabel}
          onBack={goBack}
        />
        <Error title="Страница пользователя недоступна" description={error} retryCallback={() => loadListings()} />
      </main>
    );
  }

  return (
    <main className="mx-auto grid max-w-[1344px] gap-6 px-3.5 pb-14 pt-6 md:px-6 lg:px-10 xl:px-12">
      <PageNavigation
        backLabel={listingReturnTo ? "К объявлению" : defaultBackLabel}
        onBack={goBack}
      />

      <section className="grid gap-4 border-y border-[#e8e8e8] bg-white py-5 md:grid-cols-[auto_1fr] md:items-center md:rounded-[18px] md:border md:px-5">
        {avatarUrl ? (
          <img className="h-20 w-20 rounded-full object-cover" src={avatarUrl} alt={displayName} />
        ) : (
          <DefaultAvatar name={displayName} color={profile?.avatarColor} height={80} width={80} />
        )}
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="m-0 text-[28px] font-bold leading-[1.12] tracking-[-0.02em] text-[#1a1a1a] md:text-[36px]">
              {displayName}
            </h1>
            <span className="rounded-full bg-[#f2f2f2] px-2.5 py-1 text-xs font-semibold text-[#6b6b6b]">
              ID {userId}
            </span>
          </div>
          <div className="mt-2 text-sm text-[#7a7a7a]">Дата регистрации: {registrationDate || "—"}</div>
          {isControlPanelProfile ? (
            <div className="mt-3 inline-flex items-baseline gap-2 rounded-xl bg-[#f8f8f8] px-3 py-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-[#757575]">Объявления</span>
              <span className="text-lg font-bold tabular-nums text-[#1a1a1a]">{listingsCount ?? "—"}</span>
            </div>
          ) : null}
          <div className="mt-3 flex flex-wrap gap-2 text-sm text-[#666]">
            {phone && phoneHref ? (
              <a className="inline-flex items-center gap-1.5 text-[#5f5f5f] hover:text-[#8b2a5e]" href={phoneHref}>
                <Phone size={15} />{phone}
              </a>
            ) : null}
            {email ? (
              <a className="inline-flex items-center gap-1.5 text-[#5f5f5f] hover:text-[#8b2a5e]" href={`mailto:${email}`}>
                <Mail size={15} />{email}
              </a>
            ) : null}
            {telegram ? (
              <a
                className="inline-flex items-center gap-1.5 text-[#5f5f5f] hover:text-[#8b2a5e]"
                href={`https://t.me/${telegram.replace(/^@/, "")}`}
                target="_blank"
                rel="noreferrer"
              >
                <Send size={15} />{telegram}
              </a>
            ) : null}
            {!phone && !email && !telegram ? (
              <span className="inline-flex items-center gap-1.5 text-[#8a8a8a]">
                <MapPin size={15} />Контакты не указаны
              </span>
            ) : null}
          </div>
        </div>
      </section>

      {canManageListings ? (
        <HHTabs
          activeTab={activeListingsTab}
          setActiveTab={handleListingsTabChange}
          tabs={listingTabs}
          listClassName="!rounded-[12px] !border-[#e8e8e8] !bg-white !p-1 !shadow-none"
          triggerClassName="!min-h-[38px] !rounded-[10px] !px-3.5 !py-2 !text-[13px] data-[state=active]:!bg-[#fbeaf0] data-[state=active]:!text-[#8b2a5e] data-[state=active]:!shadow-none"
        />
      ) : null}

      <section className="grid gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="m-0 text-xl font-bold text-[#1a1a1a]">{visibleTitle}</h2>
          <span className="text-sm text-[#6b6b6b]">
            Показано: <strong className="tabular-nums text-[#1a1a1a]">{visibleItems.length}/{visibleListingsCount ?? "—"}</strong>
          </span>
        </div>
        {visibleItems.length ? (
          <ul className="m-0 grid auto-rows-fr list-none grid-cols-1 gap-3 p-0 sm:gap-4 md:grid-cols-2 md:gap-4 lg:gap-[18px] xl:grid-cols-3 max-md:-mx-3.5">
            {visibleItems.map((item) => (
                <li key={`${item.type}-${item.id}`}>
                  <ListingCard
                    item={item}
                    returnTo={returnTo}
                    canManageListings={canManageListings}
                    canDeleteListings={canDeleteListings}
                    publicationPending={listingStatusPendingKeys.has(getListingKey(item))}
                    publicationError={listingStatusErrors[getListingKey(item)]}
                    deletePending={listingDeletePendingKeys.has(getListingKey(item))}
                    deleteError={listingDeleteErrors[getListingKey(item)]}
                    onTogglePublication={toggleListingPublication}
                    onDelete={deleteListing}
                  />
                </li>
              ))}
            </ul>
          ) : (
            <div className="rounded-[14px] border border-dashed border-[#e8e8e8] bg-white px-3.5 py-[30px] text-center text-[#6b6b6b]">
              {autoLoadTab === activeListingsTab && loadMorePending ? "Загружаем объявления..." : emptyText}
            </div>
        )}
      </section>

      {pagination.has_more || loadMoreError ? (
        <div className="grid justify-items-center gap-2 pt-2">
          {loadMoreError ? <p className="m-0 text-[13px] text-[#b42318]">{loadMoreError}</p> : null}
          <button
            type="button"
            className="min-w-[170px] cursor-pointer rounded-md border border-[#c8508a] bg-white px-4 py-2 text-sm font-semibold text-[#8b2a5e] transition hover:bg-[#fff2f8] disabled:cursor-wait disabled:opacity-60"
            disabled={loadMorePending || !pagination.next_cursor}
            onClick={() => loadListings(pagination.next_cursor)}
          >
            {loadMorePending ? "Загружаем..." : loadMoreError ? "Повторить" : "Показать еще"}
          </button>
        </div>
      ) : null}
    </main>
  );
};

const mapState = (state: RootState) => ({
  user: selectUserInfo(state),
  userData: selectUserData(state),
});

export const ClassifiedsUserListings = connect(mapState)(_ClassifiedsUserListings);
